/**
 * PostHog analytics allowlist — single source of truth for what our apps may send.
 *
 * When adding new PostHog events or properties:
 * 1. Add behavioural event names to BEHAVIOURAL_EVENTS in postHogEvents.ts (they feed ALLOWED_EVENTS)
 * 2. Add property key(s) to ALLOWED_APP_PROPERTIES or ALLOWED_GROUP_PROPERTIES
 * 3. Add unit test(s) in sanitizePostHogEvent.test.ts / postHogEvents.test.ts
 *
 * Other $-prefixed properties ($browser, $os, etc.) are PostHog SDK metadata and pass
 * through unchanged. URL properties ($current_url, $referrer, $pathname) have query
 * strings and hashes stripped in sanitizePostHogEvent — they are not allowlisted here.
 *
 * SDK-required properties (token, distinct_id) must never be stripped — ingest returns
 * 401 "event submitted without an api_key" if token is missing from before_send output.
 *
 * `$set` is emitted when person properties are updated on an already-identified user
 * (e.g. team or adviser profile loading after initial identify).
 *
 * Property VALUES must be primitives — sanitizePostHogEvent coerces any array/object
 * value to "[non-primitive]". Collections (e.g. active feature flags) are encoded as a
 * delimited string; money is an amount band + currency, never a raw amount.
 */

import { BEHAVIOURAL_EVENTS } from "./postHogEvents";

/** PostHog SDK built-in events our apps emit directly (pageview, identify, group, person-set). */
const POSTHOG_BUILTIN_EVENTS = [
	"$pageview",
	"$identify",
	"$set",
	"$groupidentify",
] as const;

/**
 * Event names our apps may send; unknown events are dropped by the before_send hook.
 * Behavioural events come from BEHAVIOURAL_EVENTS — the same list that types `track()` —
 * so the typed call sites and this runtime gate cannot drift.
 */
export const ALLOWED_EVENTS = new Set<string>([
	...POSTHOG_BUILTIN_EVENTS,
	...BEHAVIOURAL_EVENTS,
]);

/** SDK-injected properties that before_send must preserve (not app-passed). */
export const POSTHOG_SDK_PROPERTIES = new Set(["token", "distinct_id"]);

/** Property keys our apps explicitly pass (person props, super properties, event props). */
export const ALLOWED_APP_PROPERTIES = new Set([
	// Person / super properties
	"portal",
	"tenantId",
	"tenantName",
	"tenantSlug",
	"team",
	"teams",
	"tenantCountry",
	"isAdviser",
	"adviserRegistrationCountry",
	// Account & sign-in (§5.1)
	"invite_present",
	"mfa_used",
	"reason",
	// Identity / entity context (§5.2, §5.3, §5.4, §5.7)
	"entity_id",
	"entity_type",
	"to_entity_type",
	"error_type",
	// Subscription funnel (§5.4)
	"flow_type",
	"step_key",
	"step_index",
	"total_steps",
	"flags_active",
	"fund_id",
	"offer_id",
	"amount_band",
	"currency",
	"last_step_key",
	"entity_count",
	"total_amount_band",
	// Notifications (§5.6)
	"notification_type",
	// Portfolio & engagement (§5.7)
	"holdings_count_band",
	"document_category",
	// Admin app workflows (APP-24321)
	"account_id",
	"allocation_id",
	"distribution_id",
	"operation_id",
	"operation_type",
	"import_type",
	"calculation_type",
	"check_type",
	"access_type",
	"report_type",
	// Monthly Automations report
	"source",
	"period_days",
	"section_id",
]);

/** Property keys allowed on tenant group records via posthog.group(). */
export const ALLOWED_GROUP_PROPERTIES = new Set(["name", "country", "slug"]);
